import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CoverLetter } from "@models/documents/cover-letter";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { AgEntityTagsComponent } from "@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { CoverLettersApiService } from "@shared/services/cover-letters-api.service";
import { IPartialHeaderData } from "@shared/services/header.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { ColDef, RowSelectionOptions } from "ag-grid-community";
import { finalize, take } from "rxjs";
import { ManageTagsModalComponent } from "../manage-tags-page/manage-tags-modal/manage-tags-modal.component";
import {
  AddDocumentTemplateModalComponent
} from "./components/add-cover-letter-modal/add-document-template-modal.component";
import { AgCoverLetterActionsComponent } from "./components/ag-cover-letter-actions/ag-cover-letter-actions.component";
import { AgCoverLetterFileComponent } from "./components/ag-cover-letter-file/ag-cover-letter-file.component";

@Component({
  selector: 'app-document-templates-page',
  standalone: true,
  providers: [CoverLettersApiService, { provide: PageSizeService, useClass: PageSizeService },],
  imports: [
    ButtonComponent,
    LoaderComponent,
    HasRoleDirective,
    ListGridClientSideComponent
  ],
  templateUrl: './document-templates-page.component.html',
  styleUrls: ['./document-templates-page.component.scss', '../../styles/settings-styles.scss']
})
export class DocumentTemplatesPageComponent extends CommonPageComponent implements OnInit {
  public pageSizeService = inject(PageSizeService);
  private modalService = inject(NgbModal);
  private coverLettersService = inject(CoverLettersApiService);
  private destroyRef = inject(DestroyRef);

  override readonly headerData: IPartialHeaderData = {
    title: 'Cover Letter Templates',
    iconClass: 'ic-file-20',
    backPath: ['/settings']
  };
  checkedCoverLetters: CoverLetter[] = [];
  coverLetters: CoverLetter[] = [];
  addDocumentTemplateModalOpened = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadCoverLetters();
  }

  onManageTags(): void {
    const modalRef = this.modalService.open(ManageTagsModalComponent,
      {
        windowClass: 'manage-tags-modal',
        size: 'xl',
        centered: true
      });
    const instance = modalRef.componentInstance as ManageTagsModalComponent;

    instance.entities = this.checkedCoverLetters;

    instance.confirm
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadCoverLetters();
        this.onCoverLetterCheck([]);
      });
  }

  onCoverLetterCheck(checkedCoverLetters: CoverLetter[]): void {
    this.checkedCoverLetters = checkedCoverLetters;
  }

  createEditDocumentTemplate(editedCoverLetter: CoverLetter | null = null): void {
    if (this.addDocumentTemplateModalOpened) return;
    this.addDocumentTemplateModalOpened = true;
    const modalRef = this.modalService.open(AddDocumentTemplateModalComponent, {
      windowClass: 'add-document-template-modal',
      size: 'lg',
    });
    const instance = modalRef.componentInstance as AddDocumentTemplateModalComponent;
    instance.editedCoverLetter = editedCoverLetter;

    modalRef.result.then(
      () => {
        this.loadCoverLetters();
        this.addDocumentTemplateModalOpened = false;
      },
      () => {
        this.addDocumentTemplateModalOpened = false;
      });
  }

  downloadTemplate(coverLetter: CoverLetter): void {
    this.coverLettersService.downloadCoverLetterTemplate(coverLetter.id, coverLetter.fileName)
      .subscribe({
        next: (file) => downloadBase64File(file, coverLetter.fileName)
      });
  }

  downloadPreview(coverLetter: CoverLetter): void {
    this.coverLettersService.testCoverLetter(coverLetter.id, coverLetter.fileName)
      .subscribe({
        next: (file) => downloadBase64File(file, "test-cover-letter.pdf")
      });
  }

  downloadDefaultTemplate(): void {
    this.coverLettersService.downloadDefaultTemplate()
      .subscribe((file) => downloadBase64File(file, 'default-template.docx'));
  }

  private loadCoverLetters(): void {
    this.isLoading.set(true);

    this.coverLettersService.loadCoverLetters()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((coverLetters) => {
        this.coverLetters = coverLetters;
      });
  }

  readonly colDefs: ColDef[] = [
    {
      headerName: 'Template Name',
      flex: 2,
      field: 'title',
      sort: 'asc',
      cellRenderer: AgCoverLetterFileComponent,
      cellClass: 'cursor-text',
    },
    {
      width: 380,
      headerName: 'Tags',
      field: 'tags',
      flex: 1,
      cellRenderer: AgEntityTagsComponent,
      cellRendererParams: {
        allowEdit: true
      },
    },
    {
      width: 90,
      minWidth: 90,
      headerName: '',
      cellRenderer: AgCoverLetterActionsComponent,
      cellRendererParams: {
        downloadTemplate: this.downloadTemplate.bind(this),
        downloadPreview: this.downloadPreview.bind(this),
        edit: this.createEditDocumentTemplate.bind(this),
      },
    },
  ]
    .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  readonly documentTemplatesRowSelection: RowSelectionOptions = BaseListGridComponent.noCheckboxesRowSelection;
}
